import { ChatWidgetTroubleshootingToolIssueType } from '@mm/state/interfaces/chatWidgetTroubleshooting';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import {
  chatWidgetTroubleshootingLastResultSelector,
  chatWidgetTroubleshootingLastResultTimeSelector,
} from '@mm/state/selectors/chatWidgetTroubleshootingSelectors';
import { getUniqId } from '@mm/utils';
import { ReactComponent as ErrorIcon } from 'assets/indicator-error.svg';
import { ReactComponent as OkIcon } from 'assets/indicator-ok.svg';
import moment from 'moment';
import React from 'react';
import { useSelector } from 'react-redux';
import { PrimaryToolTip } from '../../../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import './ChatWidgetTroubleshootingToolResult.pcss';
import { useChatbotActiveResult } from './hooks/use-chatbot-active-result';
import { useConsoleResult } from './hooks/use-console-result';
import {
  useDetectConflictingCSSForFBResult,
  useDetectConflictingCSSForWebchatResult,
} from './hooks/use-detect-conflicting-css';
import { useDetectedChatWidget } from './hooks/use-detected-chat-widget';
import { useDomainWhitelistedResult } from './hooks/use-domain-whitelisted-result';
import { useFBPageAgeRestrictions } from './hooks/use-fb-page-age-restrictions';
import { useFBPageCountryRestrictions } from './hooks/use-fb-page-country-restrictions';
import { useFBPageUnpublished } from './hooks/use-fb-page-unpublished';
import { useJSLoadedResult } from './hooks/use-js-loaded-result';
import { useJSTimestampResult } from './hooks/use-js-timestamp-result';
import { usePageFinishedLoadingResult } from './hooks/use-page-finished-loading-result';
import { usePromoterActiveResult } from './hooks/use-promoter-active-result';
import { useWpRocketCombinedJSResult } from './hooks/use-wp-rocket-combined-js-result';
import { useWrongFbSdkResult } from './hooks/use-wrong-fb-sdk-result';

export type ResultSet = {
  main: Result[];
  additional: Result[];
};

export type Result =
  | {
      result: 'ok' | 'error' | 'info';
      description?: React.ReactNode;
      infoTooltip?: React.ReactNode;
    }
  | undefined;
type Props = {
  issueType: ChatWidgetTroubleshootingToolIssueType;
  promoter?: PromoterModel;
  runDiagnostic: (updatedPromoter?: PromoterModel) => Promise<void>;
  isXRay?: boolean;
};
export const ChatWidgetTroubleshootingToolResult = (props: Props) => {
  const { issueType } = props;
  const result = useSelector(chatWidgetTroubleshootingLastResultSelector);
  const resultTime = useSelector(
    chatWidgetTroubleshootingLastResultTimeSelector,
  );
  const resultSets: Record<ChatWidgetTroubleshootingToolIssueType, ResultSet> =
    {
      missing: {
        main: [
          usePageFinishedLoadingResult(),
          useJSLoadedResult(props),
          useChatbotActiveResult(props),
          usePromoterActiveResult(props),
          useFBPageUnpublished(props),
          useDomainWhitelistedResult(props),
          useJSTimestampResult(props),
        ],
        additional: [
          useFBPageCountryRestrictions(props),
          useFBPageAgeRestrictions(props),
          useDetectConflictingCSSForFBResult(props),
          useDetectConflictingCSSForWebchatResult(props),
          useWrongFbSdkResult(props),
          useWpRocketCombinedJSResult(),
          useDetectedChatWidget(),
          useConsoleResult(),
        ],
      },
      outdated: {
        main: [
          useJSLoadedResult(props),
          useJSTimestampResult(props),
          useChatbotActiveResult(props),
          usePromoterActiveResult(props),
        ],
        additional: [useDetectedChatWidget(), useWpRocketCombinedJSResult()],
      },
    };

  if (!result || !resultTime) {
    return null;
  }

  const renderAdditionalResults = (results: Result[]) => {
    if (results.filter(r => !!r).length === 0) {
      return null;
    }
    const anyErrors = !!results.find(r => r?.result === 'error');

    return (
      <>
        <div className="chat-widget-troubleshooting-tool-result__result">
          {anyErrors ? (
            <>
              <ErrorIcon />
              <span className="chat-widget-troubleshooting-tool-result__result-description">
                Additional errors found
              </span>
            </>
          ) : (
            <>
              <OkIcon />
              <span className="chat-widget-troubleshooting-tool-result__result-description">
                Additional tests performed
              </span>
            </>
          )}
        </div>
        <div
          className={`chat-widget-troubleshooting-tool-result__additional-results ${
            anyErrors
              ? 'chat-widget-troubleshooting-tool-result__additional-results--error'
              : ''
          }`}
        >
          {renderResultArray(results)}
        </div>
      </>
    );
  };
  const renderResultArray = (results: Result[]) => {
    return results.map(result => {
      const infoTooltip = result?.infoTooltip ? (
        <PrimaryToolTip
          classNameContent="chat-widget-troubleshooting-tool-result__info-tooltip"
          classNameContainer="chat-widget-troubleshooting-tool-result__info-tooltip-container"
          align="center"
          position="bottom"
          anchorElement={<i className="fa-question-circle far" />}
          enableReposition
        >
          {result.infoTooltip}
        </PrimaryToolTip>
      ) : null;
      switch (result?.result) {
        case 'info':
          return (
            <div className="chat-widget-troubleshooting-tool-result__result">
              <i className="fas fa-info-circle chat-widget-troubleshooting-tool-result__result-info-icon" />{' '}
              <span className="chat-widget-troubleshooting-tool-result__result-description">
                {result.description}
                {infoTooltip}
              </span>
            </div>
          );
        case 'ok':
          return (
            <div className="chat-widget-troubleshooting-tool-result__result">
              <OkIcon />{' '}
              <span className="chat-widget-troubleshooting-tool-result__result-description">
                {result.description}
                {infoTooltip}
              </span>
            </div>
          );
        case 'error':
          return (
            <div className="chat-widget-troubleshooting-tool-result__result">
              <ErrorIcon />{' '}
              <span className="chat-widget-troubleshooting-tool-result__result-description">
                {result.description}
                {infoTooltip}
              </span>
            </div>
          );

        case undefined:
        default:
          return null;
      }
    });
  };
  const renderResultSet = (resultSet: ResultSet) => {
    return [
      ...renderResultArray(resultSet.main),
      renderAdditionalResults(resultSet.additional),
      [...resultSet.main, ...resultSet.additional].find(
        r => r?.result === 'error',
      ) ? null : (
        <div className="chat-widget-troubleshooting-tool-result__result">
          <OkIcon />
          <span className="chat-widget-troubleshooting-tool-result__result-description">
            <strong>No issues found.</strong>&nbsp;Contact&nbsp;
            <a
              href="mailto:support@mobilemonkey.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              support@mobilemonkey.com
            </a>
            &nbsp;if you still need help with your chat widget setup.
          </span>
        </div>
      ),
    ];
  };

  return (
    <div className="chat-widget-troubleshooting-tool-result">
      <h1>Diagnostic Result</h1>
      <p>{moment(resultTime).calendar()}</p>
      <div
        className="chat-widget-troubleshooting-tool-result__results"
        key={getUniqId()}
      >
        {renderResultSet(resultSets[issueType])}
      </div>
    </div>
  );
};
